import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration, documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { EmailImportance } from "client/Api/Email/common";
import { SignatureModel } from "client/Api/Email/email";
import {
	Action,
	ActionGroup,
	ActionKind,
	SelectOptionAction
} from "client_legacy/ndaw/actions";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { EmailComposerHandle } from "../EmailContent/EmailComposer/EmailComposer";
import { isNotNil } from "client/Misc/utils";

type ActionCallbacks = {
	onIsInteractingWithBackendChange(isVisible: boolean): void;
	onOpenSignatureSettingsDialog(): void;
};

export type ComposeEmailActionOptions = {
	hasSendAction: boolean;
	hasDiscardAction: boolean;
	signatures: readonly SignatureModel[];
	callbacks: ActionCallbacks;
};

export type ComposeEmailActionContext = {
	isMobile: boolean;
	composerHandle: EmailComposerHandle;
};

export type ComposeEmailAction = Action<ComposeEmailActionContext>;

export class ComposeEmailActions {
	private readonly _options: ComposeEmailActionOptions;
	private readonly _onBeforeModifyCurrentEmail: () => boolean;

	private readonly _send: ComposeEmailAction;

	private readonly _addAttachmentFromDms: ComposeEmailAction;
	private readonly _addAttachmentFromDevice: ComposeEmailAction;
	private readonly _addImage: ComposeEmailAction;

	private readonly _signatureGroup: ActionGroup;
	private readonly _signatureSettings: ComposeEmailAction;

	private readonly _importances: Map<EmailImportance, SelectOptionAction<ComposeEmailActionContext>>;

	private readonly _discard: ComposeEmailAction;
	private readonly _discardMobile: ComposeEmailAction;

	constructor(
		options: ComposeEmailActionOptions,
		onBeforeModifyCurrentEmail: () => boolean
	) {
		this._options = options;
		this._onBeforeModifyCurrentEmail = onBeforeModifyCurrentEmail;

		this._send = {
			kind: ActionKind.Regular,
			name: "sendEmail",
			title: localize("_Send"),
			icon: "paper-plane",
			canExecute: _ => true,
			execute: (ctx) => {
				if (onBeforeModifyCurrentEmail()) {
					ctx.composerHandle
						.send();
				}
			}
		};

		const attachmentGroup: ActionGroup = {
			kind: "regular",
			name: "addAttachment",
			title: localize("_Insert"),
			icon: "paperclip"
		};

		this._addAttachmentFromDevice = {
			kind: ActionKind.Regular,
			name: "addAttachmentFromDevice",
			group: attachmentGroup,
			title: localize("_AttachmentFromDevice"),
			canExecute: _ => true,
			execute: (ctx) => {
				ctx.composerHandle
					.attachFilesFromLocalDevice();
			}
		};

		this._addAttachmentFromDms = {
			kind: ActionKind.Regular,
			name: "addAttachmentFromDms",
			group: attachmentGroup,
			title: localize("_AttachmentFromProductName")
				.replace(
					"{0}",
					userConfiguration()
						.branding
						.productName
				),
			canExecute: _ => {
				const { sharePoint, webDav } = documentsConfiguration();
				return sharePoint.isConfigured || (isNotNil(webDav) && webDav.isConfigured);
			},
			execute: (ctx) => {
				const { sharePoint, webDav } = documentsConfiguration();
				ctx.composerHandle
					.attachFilesFromDms({
						isSharepointEnabled: sharePoint.isConfigured,
						webDavConfiguration: webDav
					});
			},
		};

		this._addImage = {
			kind: ActionKind.Regular,
			name: "insertImage",
			group: attachmentGroup,
			title: localize("_Image"),
			canExecute: _ => true,
			execute: (ctx) => {
				ctx.composerHandle
					.insertInlineImage();
			}
		};

		this._signatureGroup = {
			kind: "regular",
			name: "insertSignature",
			title: localize("_Signature"),
			icon: "feather"
		};

		this._signatureSettings = {
			kind: ActionKind.Regular,
			name: "signatureSettings",
			group: this._signatureGroup,
			title: localize("_Settings"),
			canExecute: _ => true,
			execute: () => options.callbacks
				.onOpenSignatureSettingsDialog()
		};

		const importanceGroup: ActionGroup = {
			kind: "regular",
			name: "importance",
			title: localize("_Importance"),
			icon: "triangle-exclamation"
		};

		this._importances = new Map<EmailImportance, SelectOptionAction<ComposeEmailActionContext>>();
		const importanceEntries: [EmailImportance, LocalizationKey][] = [
			[EmailImportance.Low, "_Low"],
			[EmailImportance.Normal, "_Normal"],
			[EmailImportance.High, "_High"],
		];

		for (const [importance, localizationKey] of importanceEntries) {
			this._importances.set(
				importance,
				{
					kind: ActionKind.SelectOption,
					name: "importance" + localizationKey,
					group: importanceGroup,
					title: localize(localizationKey),
					canExecute: _ => true,
					execute: (ctx) => {
						ctx.composerHandle
							.setImportance(importance);
					},
					isChecked(ctx): boolean {
						return ctx.composerHandle.importance === importance;
					},
					closeMenuOnClick: true
				}
			);
		}

		const createDicardAction = (isForMobile: boolean): ComposeEmailAction => {
			return {
				kind: ActionKind.Regular,
				name: `cancelNewEmail${isForMobile ? "Mobile" : ""}`,
				title: localize("_Discard"),
				icon: "trash-can",
				canExecute: ctx => isForMobile === ctx.isMobile,
				execute: (ctx) => {
					ctx.composerHandle
						.cancel();
				}
			};
		};

		this._discardMobile = createDicardAction(true);
		this._discard = createDicardAction(false);
	}

	private getSignatureActions(): ComposeEmailAction[] {
		const { signatures } = this._options;
		return sortBy(signatures, (x) => x.kind)
			.map((signature, i) => {
				const action: ComposeEmailAction = {
					kind: ActionKind.Regular,
					name: "signature" + i,
					group: this._signatureGroup,
					title: signature.name,
					canExecute: _ => true,
					execute: (ctx) => {
						if (this._onBeforeModifyCurrentEmail()) {
							// Defer signature insertion until after the menu in action bar is closed (and the editor can regain focus),
							setTimeout(
								(): void => {
									ctx.composerHandle
										.insertSignature(signature.content);
								},
								0
							);
						}
					}
				};

				return action;
			});
	}

	public getAllActions(): ComposeEmailAction[] {
		const getImportanceAction = (
			importance: EmailImportance
		): SelectOptionAction<ComposeEmailActionContext> => {
			return {
				...this._importances.get(importance)!
			};
		};
		return [
			this._options.hasSendAction && this._send,
			this._options.hasDiscardAction && this._discardMobile,
			this._addAttachmentFromDms,
			this._addAttachmentFromDevice,
			this._addImage,
			...this.getSignatureActions(),
			this._signatureSettings,
			getImportanceAction(EmailImportance.Low),
			getImportanceAction(EmailImportance.Normal),
			getImportanceAction(EmailImportance.High),
			this._options.hasDiscardAction && this._discard
		]
			.filter((x): x is ComposeEmailAction => x !== false);
	}
}